import { useEffect, useState } from 'react';
import { apiFetch, useAuthStore } from '../stores/authStore';

/**
 * Shared subscription to GET /api/trader/headroom. Multiple consumers
 * (LimitsWidget, order-form inline preview, etc.) share a single polling
 * loop + cached value so we don't fire the request once per mounted view.
 *
 * Refresh cadence: 7s. Bumps via `bumpHeadroom()` after the caller knows
 * something changed (e.g. just placed/cancelled an order) so we don't have
 * to wait for the poll.
 */

const POLL_MS = 7_000;

export interface HeadroomSnapshot {
  maxContracts: number | null;
  maxPositions: number | null;
  openContracts: number;
  openPositions: number;
  intentContracts: number;
  /** Summary of which trader_rules dimensions are active. Only present
   *  when the relay returns a v2 headroom payload (post sport-rules deploy);
   *  pre-deploy clients see undefined and treat as "no rules active". */
  rulesActive?: {
    sportRestricted: boolean;
    nonSportsBlocked: boolean;
    polymarketBlocked: boolean;
  };
}

let cached: HeadroomSnapshot | null = null;
let pollTimer: ReturnType<typeof setInterval> | null = null;
// Bumped on logout reset — discards an in-flight fetch that began as the
// previous user so it can't repopulate `cached` (their caps/open counts) after
// the wipe and leak across a logout→login in the same tab. Headroom drives the
// LimitsWidget + order-form cap preview (a money surface), so unlike the
// rate-limit sibling this guard is load-bearing, not just hygiene. Mirrors
// useRateLimitStatus / useUserKalshiPOS.
let epoch = 0;
const listeners = new Set<(s: HeadroomSnapshot | null) => void>();

async function fetchOnce(): Promise<void> {
  // Snapshot the epoch before the await; if a logout/reset bumps it while the
  // request is in flight, discard the result instead of writing it back.
  const myEpoch = epoch;
  try {
    const res = await apiFetch('/api/trader/headroom');
    if (myEpoch !== epoch || !res.ok) return;
    const next = (await res.json()) as HeadroomSnapshot;
    if (myEpoch !== epoch) return;
    cached = next;
    listeners.forEach((l) => l(cached));
  } catch {
    // silent
  }
}

function ensurePolling(): void {
  if (pollTimer) return;
  pollTimer = setInterval(fetchOnce, POLL_MS);
}

function stopPolling(): void {
  if (pollTimer) clearInterval(pollTimer);
  pollTimer = null;
}

/** Force a fresh fetch (e.g. right after placing an order). */
export function bumpHeadroom(): void {
  fetchOnce();
}

/** Wipe the cache — used on logout so a different user doesn't see stale. */
export function resetHeadroomCache(): void {
  epoch += 1; // invalidate any in-flight fetch from the previous user.
  cached = null;
  stopPolling();
  listeners.clear();
}

export function useHeadroom(): HeadroomSnapshot | null {
  const isAuthed = useAuthStore((s) => s.status === 'authenticated');
  const [snap, setSnap] = useState<HeadroomSnapshot | null>(cached);

  useEffect(() => {
    if (!isAuthed) return;
    listeners.add(setSnap);
    // First subscriber kicks off the loop + an immediate fetch.
    if (listeners.size === 1) {
      ensurePolling();
      fetchOnce();
    } else if (cached) {
      // Late subscribers get the cached value immediately so they aren't
      // null-flickering before the next poll lands.
      setSnap(cached);
    }
    return () => {
      listeners.delete(setSnap);
      if (listeners.size === 0) stopPolling();
    };
  }, [isAuthed]);

  return snap;
}
